import React, { useState } from 'react';

import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  StripePaymentElement,
  StripePaymentElementOptions,
} from '@stripe/stripe-js';
import { IntentType } from 'src/checkout/state/checkout-types';
import { Button } from 'src/common/components/button/button';
import { Flex } from 'src/common/components/flex/flex';

import PaymentCollectionSkeleton from '../payment-collection-skeleton';
import './payment-collection.css';

type PaymentCollectionComponentProps = {
  intentType: IntentType | null;
  noTrial: boolean;
};

export const PaymentCollectionComponent: React.FC<
  PaymentCollectionComponentProps
> = ({ intentType, noTrial }) => {
  const [isInitializing, setIsInitializing] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const paymentElementOptions = {
    layout: 'tabs',
    fields: {
      billingDetails: 'auto',
    },
    terms: {
      card: 'never',
    },
  } as StripePaymentElementOptions;

  const handleElementReady = (element: StripePaymentElement) => {
    setIsInitializing(false);
    element.focus();
  };

  const handleClick = async () => {
    if (!stripe || !elements) {
      // TODO(nbermudezs): log an error, this should not happen!
      return;
    }
    setIsSubmitting(true);

    const confirmationFunction =
      intentType === 'setup' ? stripe.confirmSetup : stripe.confirmPayment;
    const { error } = await confirmationFunction({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'always',
    });

    if (error) {
      // TODO(nbermudezs): add proper error handling here...
      // this is time consuming because we need to try a bunch of different scenarios
      // and decide what we wanna do in each case.
      // For now we'll do a hand-waivy thing and just show a generic error message
      if (error.type === 'invalid_request_error') {
        // reproducible by using any of the 3DS cards and then failing the challenge
        if (error.code === 'setup_intent_authentication_failure') {
        }
      }

      if (error.type === 'validation_error') {
        // reproducible by starting the googlepay flow and then closing the modal
        if (error.code === 'incomplete') {
        }
      }

      setIsSubmitting(false);
    }
  };

  return (
    <Flex className="payment-collection">
      <PaymentElement
        options={paymentElementOptions}
        onReady={handleElementReady}
      />
      {isInitializing ? (
        <PaymentCollectionSkeleton />
      ) : (
        <Button color="primary" onClick={handleClick} disabled={isSubmitting}>
          {isSubmitting && (
            <Flex alignItems="center" className="payment-collection__loader">
              <FontAwesomeIcon
                icon={faCircleNotch}
                className="payment-collection__loader__icon"
                width={16}
              />
            </Flex>
          )}
          {noTrial ? 'Upgrade Now' : 'Start Free Trial'}
        </Button>
      )}
    </Flex>
  );
};
